import React, { useState, useEffect, useMemo } from 'react'
import styles from './ModalAddExtendedWarranty.module.css'
import PropTypes from 'prop-types'
import { t } from 'i18next'
import { Modal } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import CircularProgress from '@mui/material/CircularProgress'
import { Formatter } from 'Utils/Formatter'
import { noWarranty } from 'Utils/constants'
import No from 'Assets/Images/Login/no.svg'
import extendedWarranty from 'Assets/Images/Generals/extendedWarranty.svg'
import { useWarrantyTypes } from 'Hooks/Products/useWarrantyTypes'
import useHandleWarranty from 'Hooks/Cart/useHandleWarranty'
import { validateRepeatedProduct } from 'Utils/functions'

const ModalAddExtendedWarranty = ({ show, onClose, productInformation }) => {
  const dispatch = useDispatch()
  const { data: warrantyOptions = [], isLoading } = useWarrantyTypes()
  const { addWarranty } = useHandleWarranty()
  const cart = useSelector(state => state.cart.cart)
  const customerId = useSelector(
    state => state.cotizacion?.QuoteHeader?.CustomerId
  )

  const [selectedWarranty, setSelectedWarranty] = useState(noWarranty)

  const cartItemPrice = useMemo(
    () =>
      cart.find(
        item =>
          item.ProductId === productInformation?.ProductId &&
          !item.IsExtendedWarranty
      )?.Price,
    [cart, productInformation?.ProductId]
  )

  const enrichedWarrantyOptions = useMemo(() => {
    const price = cartItemPrice ?? productInformation?.precio
    return warrantyOptions.map(option => ({
      ...option,
      WarrantyProductId: productInformation?.ProductId,
      CalculatedPrice: (price * option.WarrantyPercent) / 100,
    }))
  }, [warrantyOptions, productInformation, cartItemPrice])

  const handleAddService = async () => {
    const isProductInCart = cart.some(
      item =>
        item.ProductId === productInformation.ProductId &&
        !item.IsExtendedWarranty
    )

    if (!isProductInCart) {
      const productToAdd = {
        ...productInformation,
        WarrantyYears: selectedWarranty.WarrantyYears,
        qty: 1,
        Quantity: 1,
      }
      await validateRepeatedProduct(productToAdd, cart, dispatch, customerId)
    }

    const existingWarranty = cart.find(
      item =>
        item.IsExtendedWarranty &&
        item.WarrantyProductId === productInformation.ProductId
    )
    const isSameWarrantyType =
      existingWarranty?.WarrantyYears === selectedWarranty.WarrantyYears
    const warrantyQuantity =
      existingWarranty && isSameWarrantyType
        ? Number(existingWarranty.Quantity) + 1
        : 1

    await addWarranty({
      selectedWarranty,
      productId: productInformation.ProductId,
      quantity: warrantyQuantity,
    })
    onClose()
  }

  useEffect(() => {
    if (show) {
      setSelectedWarranty(noWarranty)
    }
  }, [show])

  return (
    <Modal
      centered
      show={show}
      backdrop="static"
      className={styles.extendedWarrantyModal}>
      <div className={styles.extendedWarrantyModal__content}>
        <div className={styles.extendedWarrantyModal__header}>
          <button
            onClick={onClose}
            className={styles.extendedWarrantyModal__closeButton}>
            <img src={No} alt="close" />
          </button>
          <img
            src={extendedWarranty}
            alt="extended warranty icon"
            className={styles.extendedWarrantyModal__icon}
          />
          <h3 className={styles.extendedWarrantyModal__title}>
            {t('OperationsList.ExtendedWarranty')}
          </h3>
        </div>
        {isLoading ? (
          <div className={styles.extendedWarrantyModal__loading}>
            <CircularProgress />
          </div>
        ) : (
          <div className={styles.extendedWarrantyModal__detailsContainer}>
            <p className={styles.extendedWarrantyModal__detailsText}>
              {t('EXTENDED_WARRANTY_T.EXTENDED_WARRANTY_MODAL_DESCRIPTION', {
                sku: productInformation?.Sku,
              })}
            </p>
            <ul className={styles.extendedWarrantyModal__options}>
              {enrichedWarrantyOptions.map(option => (
                <li key={option.ExtendedWarrantyId}>
                  <input
                    type="radio"
                    name={'warranty-option'}
                    className="warranty-option"
                    id={`warranty-option-${option.ExtendedWarrantyId}`}
                    checked={
                      selectedWarranty?.WarrantyYears === option.WarrantyYears
                    }
                    onChange={() => setSelectedWarranty(option)}
                  />
                  <label
                    htmlFor={`warranty-option-${option.ExtendedWarrantyId}`}
                    onClick={() => setSelectedWarranty(option)}>
                    {option.WarrantyYears} {t('YEAR')}
                    {option.WarrantyYears > 1 ? 's' : ''} $
                    {Formatter.Money(option.CalculatedPrice)}
                  </label>
                </li>
              ))}
              <li>
                <input
                  type="radio"
                  name={'warranty-option'}
                  className="warranty-option"
                  id={`warranty-option-no-warranty`}
                  checked={
                    selectedWarranty?.WarrantyYears === noWarranty.WarrantyYears
                  }
                  onChange={() => setSelectedWarranty(noWarranty)}
                />
                <label
                  htmlFor={`warranty-option-no-warranty`}
                  onClick={() => setSelectedWarranty(noWarranty)}>
                  {t('EXTENDED_WARRANTY_T.NO_EXTENDED_WARRANTY')}
                </label>
              </li>
            </ul>
            <div className={styles.extendedWarrantyModal__buttonContainer}>
              <button
                className={styles.extendedWarrantyModal__applyButton}
                onClick={handleAddService}>
                {t('Apply')}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default ModalAddExtendedWarranty

ModalAddExtendedWarranty.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  productInformation: PropTypes.shape({
    ProductId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Sku: PropTypes.string,
    precio: PropTypes.number,
  }),
}
